// The playback controller: runs src/playback.ts against the real world. It feeds the machine the Director's
// speaking state, the shared video element's events and a clock, and carries out the effects (quiet the Director,
// play, ask "tap to watch", ask "what do you think?").
//
// v4 (V4-K1): `{wait}` effects settle play_and_wait calls (src/playwait.ts via director.ts `playWaits`); the end of a
// non-blocking play reaches the Director as an "[app] …" user message (finding F3), sent only when it isn't speaking;
// seekTo / playRange (review T4) hold a seek until it sticks (iOS Safari ignores early seeks, spec-v4-extras A11) and
// stop a range at its end, which counts as ended.
import { director, playWaits, tellDirector } from "./director";
import { loadUrl, mediaUnlocked, onStage, pauseStage, playUrl, stageVideo, unlockMedia } from "./media";
import { fromDirective, playedSeconds, rangeDone, reducePlay, seekStep, type PlayEffect, type PlayEvent, type PlayReq } from "./playback";
import { get, markActivity, set } from "./store";
import type { PlayDirective } from "./types";

let ticker = 0;
let wired = false;

// the seek being held until it sticks, and the range end being watched (for the request now playing)
let seekTarget: number | null = null;
let seekTries = 0;
let started = false;
let rangeEnd: number | null = null;
let rangeLoop = 0;

function wire() {
  if (wired || typeof document === "undefined") return;
  wired = true;
  onStage("playing", () => {
    started = true;
    dispatch({ type: "playing", now: Date.now() });
    playWaits.playing(get().playback.req?.token);
    holdSeek();
    watchRange();
  });
  onStage("ended", () => dispatch({ type: "ended", now: Date.now() }));
  onStage("pause", () => {
    // a Pause tap (or the `pause` tool): a play_and_wait keeps waiting, its guard stops counting
    const pb = get().playback;
    if (pb.phase === "playing" && pb.req?.wait && !stageVideo().ended) playWaits.pause(pb.req.token);
  });
  onStage("error", () => {
    const pb = get().playback;
    if (pb.phase === "playing" || pb.phase === "starting") dispatch({ type: "failed", now: Date.now() });
  });
  onStage("timeupdate", () => {
    holdSeek();
    checkRange();
  });
  // iOS Safari drops a seek made before the media is ready: make it again as soon as it is
  const v = stageVideo();
  for (const ev of ["loadedmetadata", "canplay"]) v.addEventListener(ev, holdSeek);
  // a play_and_wait whose guard ran out: drop the stuck element and give the Director its voice back
  playWaits.onSettle((s) => {
    if (s.outcome === "timeout" && get().playback.req?.token === s.token) closePlayer();
  });
  // the Director's line starting and stopping (cued plays wait for it)
  let speaking = get().director === "speaking";
  subscribeDirector((s) => {
    const now = s === "speaking";
    if (now === speaking) return;
    speaking = now;
    dispatch({ type: "director", speaking: now, now: Date.now() });
  });
}

// the store has no per-key subscription; poll the Director state cheaply while something is cued
let lastSeen: string | null = null;
let watchers: ((s: string) => void)[] = [];
function subscribeDirector(f: (s: string) => void) {
  watchers.push(f);
}
function tick() {
  const s = get().director;
  if (s !== lastSeen) {
    lastSeen = s;
    watchers.forEach((w) => w(s));
  }
  dispatch({ type: "tick", now: Date.now() });
}

export function dispatch(e: PlayEvent) {
  const before = get().playback;
  const { state, effects } = reducePlay(before, e);
  if (state !== before) set({ playback: state });
  for (const fx of effects) run(fx, before.req);
  const needsClock = state.phase === "cued" || state.phase === "starting";
  if (needsClock && !ticker) ticker = window.setInterval(tick, 150);
  if (!needsClock && ticker) {
    window.clearInterval(ticker);
    ticker = 0;
  }
  if (state.phase !== "playing" && state.phase !== "starting") stopRangeLoop();
}

/** Seconds the element has played of `req` (a range from its start). */
const elementSeconds = (req: PlayReq | null | undefined, outcome: string) => {
  if (typeof document === "undefined") return undefined;
  const v = stageVideo();
  if (outcome === "ended") {
    if (req && typeof req.end === "number") return req.end - (req.start ?? 0);
    return Number.isFinite(v.duration) && v.duration > 0 ? playedSeconds(req, v.duration) : undefined;
  }
  return playedSeconds(req, v.currentTime || 0);
};

function run(fx: PlayEffect, prevReq: PlayReq | null) {
  const req = get().playback.req;
  if (typeof fx === "object") {
    // settle the play_and_wait call this effect is about (a replaced one is the previous request)
    const about = prevReq?.token === fx.token ? prevReq : req;
    playWaits.settle(fx.token, fx.wait, fx.wait === "ended" || fx.wait === "stopped" ? elementSeconds(about, fx.wait) : undefined);
    return;
  }
  switch (fx) {
    case "load":
      if (req) loadUrl(req.url);
      break;
    case "play":
      if (req) {
        started = false;
        seekTarget = req.start ?? null;
        seekTries = 0;
        rangeEnd = req.end ?? null;
        playUrl(req.url).catch((err: unknown) => {
          // NotAllowedError: autoplay blocked. AbortError: a newer play() replaced this one (ignore).
          if ((err as Error)?.name !== "AbortError") dispatch({ type: "rejected", now: Date.now() });
        });
        if (seekTarget !== null) applySeek();
      }
      break;
    case "quiet":
      set({ directorQuiet: true });
      break;
    case "release":
      set({ directorQuiet: false });
      break;
    case "interrupt":
      director.interrupt();
      break;
    case "say_tap":
      tellDirector(`[app] ${req?.title ?? "The video"} is ready, but the browser needs a tap before it can play. Say "tap to watch" and nothing more.`);
      break;
    case "ask":
      // F3: a user message, so the Director really answers (a contextual update promises no reply)
      tellDirector(`[app] finished playing ${req?.what === "render" ? "the ad" : req?.what === "audio" ? "the audio" : "the clip"}${req?.title ? ` "${req.title}"` : ""}${req?.version ? ` (version ${req.version})` : ""} on the user's screen. Ask what they think, in a few words.`);
      break;
  }
}

// ------------------------------------------------------------------ seeks and ranges

function applySeek() {
  if (seekTarget === null) return;
  seekTries++;
  try {
    stageVideo().currentTime = seekTarget;
  } catch {
    /* not seekable yet: loadedmetadata / canplay try again */
  }
}

/** Re-apply the held seek until the element is playing at its target (or gives up). */
function holdSeek() {
  if (seekTarget === null) return;
  const step = seekStep(seekTarget, stageVideo().currentTime || 0, started, seekTries);
  if (step === "seek") applySeek();
  else if (step === "done") seekTarget = null;
}

function checkRange() {
  if (rangeEnd === null || get().playback.phase !== "playing") return;
  const v = stageVideo();
  if (!rangeDone(v.currentTime || 0, rangeEnd)) return;
  rangeEnd = null;
  stopRangeLoop();
  v.pause();
  dispatch({ type: "ended", now: Date.now() });
}

// timeupdate fires only a few times a second: poll each frame while a range plays, so it stops on its mark
function watchRange() {
  if (rangeEnd === null || rangeLoop || typeof requestAnimationFrame === "undefined") return;
  const loop = () => {
    rangeLoop = 0;
    if (rangeEnd === null) return;
    checkRange();
    if (rangeEnd !== null) rangeLoop = requestAnimationFrame(loop);
  };
  rangeLoop = requestAnimationFrame(loop);
}
function stopRangeLoop() {
  if (rangeLoop && typeof cancelAnimationFrame !== "undefined") cancelAnimationFrame(rangeLoop);
  rangeLoop = 0;
}

/** Jump the playing (or loaded) item to `s` seconds, holding the seek until it sticks. */
export function seekTo(s: number) {
  if (typeof document === "undefined" || !Number.isFinite(s)) return;
  const v = stageVideo();
  if (!v.currentSrc && !v.getAttribute("src")) return;
  markActivity();
  if (rangeEnd !== null && s >= rangeEnd) rangeEnd = null;
  seekTarget = Math.max(0, s);
  seekTries = 0;
  started = !v.paused;
  applySeek();
}

/**
 * Play `start`..`end` seconds of `req` (or of what's playing now), then stop: reaching `end` counts as ended
 * (a play_and_wait over a range settles `ended`). A tap path: it plays now. Returns false with nothing to play.
 */
export function playRange(start: number, end: number, req?: Omit<PlayReq, "user" | "then"> & { then?: PlayReq["then"] }): boolean {
  const base = req ?? get().playback.req;
  if (!base?.url || !(end > start)) return false;
  const { wait: _w, token: _t, ...rest } = base;
  playNow({ ...rest, start: Math.max(0, start), end });
  return true;
}

/** The `pause` / `resume` tools and the stage's buttons: a play_and_wait keeps waiting while paused. */
export function pausePlayback() {
  pauseStage();
}
export function resumePlayback() {
  const pb = get().playback;
  if (pb.phase !== "playing" && pb.phase !== "starting") return;
  void stageVideo()
    .play()
    ?.catch(() => undefined);
}

// ------------------------------------------------------------------ requests

/** Whether a Director-started play can sound now (a tap unlocked media, something already plays, or the page has
 *  had a gesture). Without any of these the tap-to-play gate shows straight away. */
function canAutoplay() {
  if (typeof document === "undefined") return true;
  if (mediaUnlocked() || !stageVideo().paused) return true;
  const ua = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
  return !!ua?.hasBeenActive;
}

/** The Director's `play` directive. */
export function playDirective(d: PlayDirective) {
  wire();
  if (!d.url) return;
  dispatch({ type: "request", req: fromDirective(d), now: Date.now(), directorSpeaking: get().director === "speaking", unlocked: canAutoplay() });
}

/** The user tapped play (a gesture: plays now, and interrupts the Director if it's talking). */
export function playNow(req: Omit<PlayReq, "user" | "then"> & { then?: PlayReq["then"] }) {
  wire();
  unlockMedia();
  markActivity();
  dispatch({ type: "request", req: { then: "none", ...req, user: true }, now: Date.now(), directorSpeaking: get().director === "speaking" });
}

/** The big tap-to-play on the fallback. */
export function tapToPlay() {
  unlockMedia();
  markActivity();
  dispatch({ type: "tap", now: Date.now() });
}

export function closePlayer() {
  pauseStage();
  dispatch({ type: "close", now: Date.now() });
}

/** For screenshots and tests of the fallback: show the tap-to-play state for a request. */
export function forceBlocked(req: Omit<PlayReq, "user" | "then">) {
  wire();
  set({ playback: { phase: "blocked", req: { ...req, user: false, then: "ask" }, at: Date.now(), heard: false, speaking: false, lineEnd: null, quiet: false } });
}
